import { useEffect, useMemo, useState } from 'react'
import { useDropzone, FileRejection } from 'react-dropzone'
import { LinearProgress, Grid, Typography, SxProps, IconButton, useTheme, Button, Box } from '@mui/material'
import { sluggify } from '../utils/misc'
import { TiDeleteOutline } from 'react-icons/ti'
import { MdPreview, MdFileUpload } from 'react-icons/md'

type DropZonePropsType = {
  message?: string
  maxFiles: number
  maxSize: number
  accept: Record<string, string[]>
  bucketName: string
  sx?: SxProps
  onUploaded: (data: string) => void
  folderPath?: string
  fileName?: (file: File) => string
  required?: boolean
  existingFileUrl?: string
}

const DropZone = ({
  message = 'Déposez votre fichier ou cliquez ici',
  maxFiles,
  maxSize,
  accept,
  bucketName,
  sx,
  onUploaded,
  folderPath,
  fileName,
  required = false,
  existingFileUrl,
}: DropZonePropsType) => {
  const theme = useTheme()
  const errorMapping: Record<string, string> = {
    'file-invalid-type': 'Fichier non valide',
    'file-too-large': 'Fichier trop volumineux',
    'file-too-small': 'Fichier trop petit',
    'too-many-files': 'Fichiers trop nombreux',
  }

  const { isFocused, isDragAccept, isDragReject, acceptedFiles, fileRejections, getRootProps, getInputProps, open } =
    useDropzone({
      maxFiles: maxFiles,
      maxSize: maxSize * 1e6,
      accept: accept,
    })

  const baseStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: '4px!important',
    borderColor: theme.palette.grey[500],
    borderStyle: 'dashed',
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    transition: 'border .24s ease-in-out',
    cursor: 'pointer',
    ...sx,
  }

  const focusedStyle = {
    borderColor: theme.palette.primary.main,
  }

  const acceptStyle = {
    borderColor: theme.palette.success.light,
  }

  const rejectStyle = {
    borderColor: theme.palette.error.main,
  }

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused && focusedStyle),
      ...(isDragAccept && acceptStyle),
      ...(isDragReject && rejectStyle),
    }),
    [isFocused, isDragAccept, isDragReject],
  )

  const [dropedFiles, setDropedFiles] = useState<File[]>([])
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([])
  const [progress, setProgress] = useState<Record<string, number>>({})

  // Fonction pour extraire le nom du fichier depuis l'URL
  const getFileNameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url)
      const pathname = urlObj.pathname
      const fileName = pathname.split('/').pop() || 'fichier'
      // Décoder l'URL si nécessaire
      return decodeURIComponent(fileName)
    } catch {
      // Si l'URL n'est pas valide, essayer d'extraire le nom depuis la fin de la chaîne
      const parts = url.split('/')
      return parts[parts.length - 1] || 'fichier'
    }
  }

  useEffect(() => {
    setDropedFiles((prevState) => {
      if (maxFiles <= prevState.length) return prevState
      // acceptedFiles => add to dropped files if not already there AND not in excess of maxFiles
      const filteredFiles = acceptedFiles.filter((file) => !dropedFiles.map((f) => f.name).includes(file.name))
      return [...prevState, ...filteredFiles]
    })
  }, [acceptedFiles])

  useEffect(() => {
    setRejectedFiles((prevState) => {
      const filteredFiles = fileRejections.filter(
        (fileError) => !rejectedFiles.map((f) => f.file.name).includes(fileError.file.name),
      )
      return prevState?.concat(filteredFiles)
    })
  }, [fileRejections])

  useEffect(() => {
    dropedFiles.map((file) => {
      uploadFile(file)
    })
  }, [dropedFiles])

  const removeFile = (fileName: string) => {
    setDropedFiles((prevState) => {
      return prevState.filter((file) => file.name !== fileName)
    })
    setRejectedFiles((prevState) => {
      return prevState.filter((f) => f.file.name !== fileName)
    })
  }

  const uploadFileWithXHR = (file: File, bucketName: string, filePath: string) => {
    const xhr = new XMLHttpRequest()
    const url = `https://${bucketName}.s3.eu-west-3.amazonaws.com/${filePath}`

    xhr.open('PUT', url, true)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.setRequestHeader('x-amz-acl', 'public-read')

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable) {
        const percent = Math.round((evt.loaded / evt.total) * 100)
        setProgress((prevState) => ({
          ...prevState,
          [file.name]: percent,
        }))
      }
    }

    xhr.onload = () => {
      if (xhr.status === 200) {
        const fileUrl = `https://${bucketName}.s3.eu-west-3.amazonaws.com/${filePath}`
        onUploaded(fileUrl)
        // Supprimer le fichier de la liste après upload réussi
        setDropedFiles((prevState) => prevState.filter((f) => f.name !== file.name))
        setProgress((prevState) => {
          const newProgress = { ...prevState }
          delete newProgress[file.name]
          return newProgress
        })
      } else {
        console.error('Upload failed:', xhr.status, xhr.statusText)
      }
    }

    xhr.onerror = () => {
      console.error('XHR error:', xhr.statusText)
    }

    xhr.send(file)
  }

  const uploadFile = (file: File) => {
    const storedName = fileName ? fileName(file) : sluggify(file.name)
    const filePath = folderPath ? `${folderPath}/${storedName}` : storedName
    uploadFileWithXHR(file, bucketName, filePath)
  }

  return (
    <Grid id="dropZone" item container xs={12} sx={style} {...getRootProps({ className: 'DropZone' })}>
      <input {...getInputProps()} />

      {/* Affichage du fichier existant - seulement si pas de fichiers en cours d'upload */}
      {existingFileUrl && existingFileUrl.trim() !== '' && dropedFiles.length === 0 && (
        <Grid item container direction="column" alignItems="center" justifyContent="center" spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{
                background: theme.palette.background.paper,
                padding: theme.spacing(2.5),
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
                minWidth: '300px',
                maxWidth: '500px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.primary.main,
                  }}
                >
                  <MdPreview size={20} />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    flexGrow: 1,
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    wordBreak: 'break-word',
                  }}
                >
                  {getFileNameFromUrl(existingFileUrl)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: theme.spacing(1), justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  startIcon={<MdPreview />}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    window.open(existingFileUrl, '_blank')
                  }}
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 2,
                    py: 0.5,
                    fontSize: '0.875rem',
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: theme.palette.primary.light + '20',
                    },
                  }}
                >
                  Prévisualiser
                </Button>
                <Button
                  size="small"
                  startIcon={<MdFileUpload />}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    open()
                  }}
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 2,
                    py: 0.5,
                    fontSize: '0.875rem',
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  Remplacer
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Fichier vide - seulement si pas de fichiers en cours d'upload */}
      {(!existingFileUrl || existingFileUrl.trim() === '') &&
        dropedFiles.length === 0 &&
        rejectedFiles.length === 0 && (
          <Grid item container direction="column" alignItems="center" justifyContent="center">
            <Typography variant="body1" style={{ textAlign: 'center', color: theme.palette.text.primary }}>
              {message}
              {required ? ' *' : ''}
            </Typography>
            <Typography variant="caption">{Object.values(accept).flat().join(', ')}</Typography>
          </Grid>
        )}
      {rejectedFiles.length !== 0 && (
        <Grid item container direction="row" justifyContent="center" alignItems="center">
          {rejectedFiles.map((rejection) => {
            return (
              <Grid
                item
                key={rejection.file.name}
                sx={{
                  background: theme.palette.background.default,
                  p: 1,
                  m: 1,
                  borderRadius: '4px!important',
                  position: 'relative',
                }}
              >
                <Typography variant="body2">{rejection.file.name}</Typography>
                {rejection.errors.map((error) => {
                  return (
                    <Typography key={error.code} variant="caption" sx={{ color: theme.palette.error.main }}>
                      {errorMapping[error.code] || 'Erreur fichier'}
                    </Typography>
                  )
                })}
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: -12, right: -12 }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    removeFile(rejection.file.name)
                  }}
                >
                  <TiDeleteOutline />
                </IconButton>
              </Grid>
            )
          })}
        </Grid>
      )}

      {/* Affichage des fichiers en cours d'upload */}
      {dropedFiles.length !== 0 && (
        <Grid item container direction="column" alignItems="center" justifyContent="center" spacing={2}>
          {dropedFiles.map((file) => {
            return (
              <Grid
                item
                key={file.name}
                sx={{
                  background: theme.palette.background.paper,
                  padding: theme.spacing(2),
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.primary.light}`,
                  minWidth: '300px',
                  maxWidth: '500px',
                  position: 'relative',
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Upload en cours: {file.name}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress[file.name] || 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                    },
                  }}
                />
                <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  {progress[file.name] || 0}%
                </Typography>
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: -12, right: -12, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    removeFile(file.name)
                  }}
                >
                  <TiDeleteOutline />
                </IconButton>
              </Grid>
            )
          })}
        </Grid>
      )}
    </Grid>
  )
}

export default DropZone
